import {
  IdeationMapNodeSchema,
  SubjectAssociationListSchema,
  SubjectAssociationSchema,
  SubjectCaptureNoteResultSchema,
  SubjectCaptureQuestionResultSchema,
  type SubjectAssociation,
  type SubjectAssociationCreate,
  type SubjectAssociationObjectType,
  type SubjectCaptureDecision,
  type SubjectCaptureFact,
  type SubjectCaptureNote,
  type SubjectCaptureQuestion,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { staffTenantCacheScope } from "../lib/staff-tenant-cache.js";

import { apiFetch } from "./client.js";
import { SUBJECT_KEY } from "./subjects.js";

const SUBJECT_ASSOC_KEY = ["subject-associations"] as const;

function assocKey(...parts: Array<string | null | undefined>) {
  return [...SUBJECT_ASSOC_KEY, staffTenantCacheScope(), ...parts] as const;
}

export function useSubjectAssociations(subjectId: string | null) {
  return useQuery({
    queryKey: assocKey("subject", subjectId),
    enabled: Boolean(subjectId),
    queryFn: () =>
      apiFetch(`/subjects/${subjectId}/associations`, {}, SubjectAssociationListSchema),
  });
}

export function useObjectSubjectAssociations(
  objectType: SubjectAssociationObjectType | null,
  objectId: string | null,
) {
  return useQuery({
    queryKey: assocKey("object", objectType, objectId),
    enabled: Boolean(objectType && objectId),
    queryFn: () =>
      apiFetch(
        `/subjects/object-associations?objectType=${objectType}&objectId=${objectId}`,
        {},
        SubjectAssociationListSchema,
      ),
  });
}

export function useCreateSubjectAssociation(subjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubjectAssociationCreate) =>
      apiFetch(
        `/subjects/${subjectId}/associations`,
        { method: "POST", body: JSON.stringify(input) },
        SubjectAssociationSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_ASSOC_KEY });
    },
  });
}

export function useLinkObjectToSubject(objectType: SubjectAssociationObjectType, objectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subjectId: string) =>
      apiFetch(
        `/subjects/${subjectId}/associations`,
        {
          method: "POST",
          body: JSON.stringify({ objectType, objectId }),
        },
        SubjectAssociationSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_ASSOC_KEY });
    },
  });
}

export function useArchiveSubjectAssociation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(
        `/subjects/associations/${id}/archive`,
        { method: "POST" },
        SubjectAssociationSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_ASSOC_KEY });
    },
  });
}

export function useCaptureFact(subjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubjectCaptureFact) =>
      apiFetch(
        `/subjects/${subjectId}/capture/fact`,
        { method: "POST", body: JSON.stringify(input) },
        IdeationMapNodeSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_KEY });
    },
  });
}

export function useCaptureQuestion(subjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubjectCaptureQuestion) =>
      apiFetch(
        `/subjects/${subjectId}/capture/question`,
        { method: "POST", body: JSON.stringify(input) },
        SubjectCaptureQuestionResultSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_KEY });
    },
  });
}

export function useCaptureDecision(subjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubjectCaptureDecision) =>
      apiFetch(
        `/subjects/${subjectId}/capture/decision`,
        { method: "POST", body: JSON.stringify(input) },
        IdeationMapNodeSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_KEY });
    },
  });
}

export function useCaptureNote(subjectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubjectCaptureNote) =>
      apiFetch(
        `/subjects/${subjectId}/capture/note`,
        { method: "POST", body: JSON.stringify(input) },
        SubjectCaptureNoteResultSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBJECT_KEY });
      void queryClient.invalidateQueries({ queryKey: SUBJECT_ASSOC_KEY });
    },
  });
}

export type { SubjectAssociation };
